

import { useEffect, useRef, useState, useMemo } from "react";
import Select from "react-select";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Save, AlertCircle, FileText, X, MessageSquare } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../../utility/apiInstance";
import { MILESTONE, TabFirstMileProps } from "./types/MilestoneType";
import { FileWithMeta, MilestoneOption, RowActionsProps } from "./types/FirstMilestone";
import { getMinDateTime, toDateTimeLocal } from "./utils";
import { convertToDisplayDateFormat } from "./dateUtils";
import {
  MilestoneCommentDrawer,
  MilestoneComment,
  CommentAttachment,
} from "./MilestoneCommentDrawer"; // ← reusable drawer
import { s, selectStyles } from "./stylesTsx/Style.FirstAndLastMilesontes";

function RowActions({ showDelete, showAdd, onAdd, onDelete }: RowActionsProps) {
  if (!showDelete && !showAdd) return null;
  return (
    <div style={s.rowActions}>
      {showAdd && (
        <button
          style={{ ...s.rowActionBtn, background: "#dcfce7", color: "#16a34a" }}
          onClick={onAdd} title="Add row"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}
      {showDelete && (
        <button
          style={{ ...s.rowActionBtn, background: "#fee2e2", color: "#dc2626" }}
          onClick={onDelete} title="Remove row"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

interface ErrorHintProps { message?: string }
function ErrorHint({ message }: ErrorHintProps) {
  if (!message) return null;
  return (
    <div style={s.errText}>
      <AlertCircle size={10} />
      {message}
    </div>
  );
}

// ── Comment count cache (milestoneId → count) ─────────────────────────────────

type CommentCountMap = Record<string, number>;

// ── Main Component ────────────────────────────────────────────────────────────

function TabFirstMile({
  firstmilestonesFormData = [],
  setFirstMilestonesFormData,
  groupMilestone,
  type,
  trackingNumber,
  uniqueValue,
  trackingId
}: TabFirstMileProps) {
  const [errors, setErrors] = useState<Record<string, Partial<Record<keyof MILESTONE, string>>>>({});
  const [requiredData, setRequiredData] = useState<MilestoneOption[]>([]);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Comment drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrawerMilestone, setActiveDrawerMilestone] = useState<{ id: string; name: string } | null>(null);
  const [commentCounts, setCommentCounts] = useState<CommentCountMap>({});

  const isSavingRef = useRef(false);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchData = async () => {
    try {
      const res = await axiosInstance.post(`/api/v1/groupingMileStone/milestone`, {
        type, trackingNumber, id: groupMilestone, uniqueValue
      });
      const data: MILESTONE[] = res?.data?.data?.firstmilestones || [];
      if (data.length > 0) {
        setFirstMilestonesFormData(
          data.map((f, i) => ({
            _id: f._id || `${i + 1}`,
            name: f.name,
            actualdate: toDateTimeLocal(f?.actualdate || "") || "",
            estimateddate: toDateTimeLocal(f?.estimateddate || "") || "",
            remarks: f?.remarks || "",
            files: (f?.files || []).map((url: string) => ({
              file: null, url,
              key: decodeURIComponent(new URL(url).pathname.split("/").pop() || ""),
              progress: 100,
            })),
            isNew: !f.name || (toDateTimeLocal(f?.actualdate || "") || "") === "",
            uniqueValue: uniqueValue,
            isCommentOf:false,
          }))
        );
      } else {
        setFirstMilestonesFormData([{
          _id: Date.now().toString(),
          name: "", actualdate: "", estimateddate: "", remarks: "", files: [], isNew: true,
          uniqueValue: uniqueValue,
          isCommentOf:true,
        }]);
      }
    } catch (err) { console.error(err); }
  };

  const getMilestones = async () => {
    try {
      const url = type === "ocean"
        ? `api/v1/ocean-manual-milestone?type=first`
        : `api/v1/awb-manual-milestone?type=first`;
      const { data } = await axiosInstance.get(url);
      setRequiredData(
        (data?.data || []).map((el: any) => ({ label: el?.name || "", value: el.name, isDisabled: false }))
      );
    } catch (err) { console.error(err); }
  };

  /** Load comment counts for all milestone rows (badge display) */
  const fetchAllCommentCounts = async (milestoneIds: string[]) => {
    try {
      console.log("milestoneIds",milestoneIds)
      const results = await Promise.allSettled(
        milestoneIds.map(id =>
          axiosInstance
            .get(`/api/v1/milestone-comments/${id}`)
            .then(r => ({ id, count: (r?.data?.data || []).length }))
        )
      );
      const counts: CommentCountMap = {};
      results.forEach(r => {
        if (r.status === "fulfilled") counts[r.value.id] = r.value.count;
      });
      setCommentCounts(counts);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Failed to load comment counts");
    }
  };

  useEffect(() => { fetchData(); }, [setFirstMilestonesFormData, groupMilestone]);
  useEffect(() => { getMilestones(); }, []);
  useEffect(() => {
    const ids = firstmilestonesFormData.map(m => m._id).filter(Boolean) as string[];
    if (ids.length > 0) fetchAllCommentCounts(ids);
  }, [firstmilestonesFormData.length]);

  // ── Memoized lookups ────────────────────────────────────────────────────────

  const mergedOptions = useMemo((): MilestoneOption[] => {
    const masterValues = new Set(requiredData.map(o => o.value));
    const orphanOptions: MilestoneOption[] = firstmilestonesFormData
      .filter(m => m.name && !masterValues.has(m.name))
      .map(m => ({ label: m.name, value: m.name, isDisabled: false }));
    return [...requiredData, ...orphanOptions];
  }, [requiredData, firstmilestonesFormData]);

  const optionByValue = useMemo((): Record<string, MilestoneOption> => {
    return Object.fromEntries(mergedOptions.map(o => [o.value, o]));
  }, [mergedOptions]);

  const selectedNamesSet = useMemo((): Set<string> => {
    return new Set(firstmilestonesFormData.map(m => m.name).filter(Boolean));
  }, [firstmilestonesFormData]);

  // ── Validation ──────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    let isValid = true;
    const newErrors: Record<string, any> = {};
    firstmilestonesFormData.filter(m => m.isNew).forEach(m => {
      const e: any = {};
      if (!m.name) e.name = "Name is required";
      if (Object.keys(e).length > 0) { newErrors[m._id!] = e; isValid = false; }
    });
    setErrors(newErrors);
    if (!isValid) toast.error("Please fill all required fields!");
    return isValid;
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (isSavingRef.current) return;
    if (!validate()) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await axiosInstance.post(`/api/v1/offline-first-last-milestone/create`, {
        service: type,
        milestones: firstmilestonesFormData.map(m => ({
          awbNumber: trackingNumber, _id: m._id,
          name: m?.name || "",
          remarks: m?.remarks,
          estimateddate: m?.estimateddate, actualdate: m?.actualdate,
          files: m?.files?.map(f => f.url) || [],
          type: "first",
          uniqueValue: m.uniqueValue
        })),
      });
      toast.success("Milestones saved successfully!");
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save milestones!");
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleAdd = () => {
    const last = firstmilestonesFormData[firstmilestonesFormData.length - 1];
    if (last && !last.name) { toast.error("Please fill Name before adding a new row"); return; }
    setFirstMilestonesFormData(prev => [...prev, {
      _id: Date.now().toString(), name: "", actualdate: "",
      estimateddate: "", remarks: "", files: [], isNew: true,
      isCommentOf:true,
      uniqueValue: uniqueValue
    }]);
  };

  const handleDelete = async (id: string) => {
    const m = firstmilestonesFormData.find(f => f._id === id);
    if (m?.isNew) {
      try { await axiosInstance.delete(`/api/v1/offline-first-last-milestone/delete/${m._id}`); }
      catch { toast.error("Failed to delete Milestone!"); return; }
    }
    setFirstMilestonesFormData(prev => prev.filter(m => m._id !== id));
  };

  const handleChange = (id: string, field: keyof MILESTONE, value: any) => {
    setFirstMilestonesFormData(prev => prev.map(m => m._id === id ? { ...m, [field]: value } : m));
    setErrors(prev => ({ ...prev, [id]: { ...prev[id], [field]: "" } }));
  };

  const handleFileUpload = async (id: string, files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFirstMilestonesFormData(prev => prev.map(m =>
        m._id === id ? { ...m, files: [...(m.files || []), { file, url: "", key: "", progress: 0 }] } : m
      ));
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axiosInstance.post("/api/v1/s3/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => {
            const pct = Math.round((ev.loaded * 100) / (ev.total || 1));
            setFirstMilestonesFormData(prev => prev.map(m =>
              m._id === id ? { ...m, files: (m.files || []).map(f => f.file === file ? { ...f, progress: pct } : f) } : m
            ));
          },
        });
        setFirstMilestonesFormData(prev => prev.map(m =>
          m._id === id ? {
            ...m,
            files: (m.files || []).map(f =>
              f.file === file ? { file, url: data.data.url, key: data.data.key, progress: 100 } : f
            ),
          } : m
        ));
        toast.success(`${file.name} uploaded`);
      } catch {
        toast.error(`${file.name} upload failed`);
        setFirstMilestonesFormData(prev => prev.map(m =>
          m._id === id ? { ...m, files: (m.files || []).filter(f => f.file !== file) } : m
        ));
      }
    }
  };

  const handleFileDelete = async (milestoneId: string, fileUrl: string) => {
    try {
      const fileKey = decodeURIComponent(new URL(fileUrl).pathname.split("/").pop() || "");
      await axiosInstance.post(`/api/v1/s3/delete`, { fileKey });
      setFirstMilestonesFormData(prev => prev.map(m =>
        m._id === milestoneId ? { ...m, files: (m.files || []).filter(f => f.url !== fileUrl) } : m
      ));
    } catch { toast.error("Failed to delete file"); }
  };

  const getOptionsWithDisabled = (currentId: string): MilestoneOption[] => {
    const currentName = firstmilestonesFormData.find(m => m._id === currentId)?.name;
    return mergedOptions.filter(o =>
      !selectedNamesSet.has(o.value) || o.value === currentName
    );
  };

  // ── Comment drawer handlers ─────────────────────────────────────────────────

  const openCommentDrawer = (id: string, name: string) => {
    setActiveDrawerMilestone({ id, name });
    setDrawerOpen(true);
  };

  const fetchCommentsForMilestone = async (milestoneId: string): Promise<MilestoneComment[]> => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/milestone-comments/${milestoneId}`
      );

      return res?.data?.data || [];
    } catch (err: any) {
      throw new Error(err?.response?.data?.message || err?.response?.data?.error || "Failed to save milestones!");

    }
  };



  const saveCommentForMilestone = async (
    milestoneId: string,
    text: string,
    attachments: CommentAttachment[]
  ): Promise<MilestoneComment> => {
    try {
      const res = await axiosInstance.post("/api/v1/milestone-comments", {
        milestoneId,
        text,
        attachments,
        type: "first",
        trackingId
      });

      const saved: MilestoneComment = res?.data?.data;

      // Update badge count only after successful save
      setCommentCounts((prev) => ({
        ...prev,
        [milestoneId]: (prev[milestoneId] || 0) + 1,
      }));

      return saved;
    } catch (err: any) {
      throw new Error(err?.response?.data?.message || err?.response?.data?.error || "Failed to save milestones!");

    }
  };

  const deleteCommentForMilestone = async (milestoneId: string, commentId: string) => {
    await axiosInstance.delete(`/api/v1/milestone-comments/${commentId}`);
    setCommentCounts(prev => ({ ...prev, [milestoneId]: Math.max(0, (prev[milestoneId] || 1) - 1) }));
  };

  const uploadFileForComment = async (
    file: File,
    onProgress: (pct: number) => void
  ): Promise<CommentAttachment> => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axiosInstance.post("/api/v1/s3/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: ev => onProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
    });
    return {
      url: data.data.url,
      key: data.data.key,
      name: file.name,
    };
  };

  const newCount = firstmilestonesFormData.filter(m => m.isNew).length;
  const isSaveDisabled = newCount === 0 || isSaving;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={s.root}>

      {/* Section header */}
      <div style={s.sectionHeader}>
        <div style={s.sectionTitle}>
          <div style={s.sectionAccent} />
          <span style={s.sectionLabel}>First MileStones</span>
        </div>
        <span style={s.rowCount}>
          {firstmilestonesFormData.length} milestone{firstmilestonesFormData.length !== 1 ? "s" : ""}
          {newCount > 0 && ` · ${newCount} pending`}
        </span>
      </div>

      {/* Column headers */}
      <div style={s.colHeaders}>
        {["Milestone", "Estimated", "Actual", "Remarks", "Attachment", ""].map((h, i) => (
          <span key={i} style={s.colHeader}>{h}</span>
        ))}
      </div>

      {/* Rows */}
      {firstmilestonesFormData.map((milestone: MILESTONE) => {
        const isHovered = hoveredRow === milestone._id;
        const hasError = !!errors[milestone._id!];
        const rowCardStyle: React.CSSProperties = {
          ...s.rowCard,
          ...(milestone.isNew ? s.rowCardNew : s.rowCardSaved),
          ...(hasError ? s.rowCardError : {}),
          ...(isHovered ? { borderColor: "#e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" } : {}),
        };

        const showSelect = milestone.isNew;
        const showBadge = !milestone.isNew;
        const commentCount = commentCounts[milestone._id!] || 0;

        return (
          <div
            key={milestone._id}
            style={s.rowWrap}
            onMouseEnter={() => setHoveredRow(milestone._id)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            <div style={rowCardStyle}>

              {/* Milestone name */}
              <div style={s.cell}>
                {groupMilestone ? (
                  <div style={s.nameBadge}>
                    <span style={s.nameDot} />
                    {milestone.name}
                  </div>
                ) : showSelect ? (
                  <>
                    <Select
                      styles={selectStyles(!!errors[milestone._id!]?.name)}
                      options={getOptionsWithDisabled(milestone._id)}
                      placeholder="Select milestone"
                      value={optionByValue[milestone.name] ?? null}
                      onChange={sel => handleChange(milestone._id, "name", sel?.value || "")}
                    />
                    <ErrorHint message={errors[milestone._id!]?.name} />
                  </>
                ) : showBadge ? (
                  <div style={s.nameBadge}>
                    <span style={s.nameDot} />
                    {milestone.name}
                  </div>
                ) : null}
              </div>

              {/* Estimated date */}
              <div style={s.cell}>
                {milestone.isNew ? (
                  <input
                    type="datetime-local"
                    style={s.dateInput}
                    value={milestone.estimateddate || ""}
                    min={getMinDateTime()}
                    onMouseDown={(e: any) => { e.preventDefault(); e.target.showPicker(); }}
                    onChange={e => handleChange(milestone._id, "estimateddate", e.target.value)}
                  />
                ) : (
                  <>
                    <div style={s.dateDisplay}>{convertToDisplayDateFormat(milestone.estimateddate || "").split(",")[0]}</div>
                    <div style={s.dateLabel}>{convertToDisplayDateFormat(milestone.estimateddate || "").split(",")[1]?.trim()}</div>
                  </>
                )}
                <ErrorHint message={errors[milestone._id!]?.estimateddate} />
              </div>

              {/* Actual date */}
              <div style={s.cell}>
                {milestone.isNew ? (
                  <input
                    type="datetime-local"
                    style={s.dateInput}
                    value={milestone.actualdate || ""}
                    min={getMinDateTime()}
                    onMouseDown={(e: any) => { e.preventDefault(); e.target.showPicker(); }}
                    onChange={e => handleChange(milestone._id, "actualdate", e.target.value)}
                  />
                ) : (
                  <>
                    <div style={s.dateDisplay}>{convertToDisplayDateFormat(milestone.actualdate || "").split(",")[0]}</div>
                    <div style={s.dateLabel}>{convertToDisplayDateFormat(milestone.actualdate || "").split(",")[1]?.trim()}</div>
                  </>
                )}
                <ErrorHint message={errors[milestone._id!]?.actualdate} />
              </div>

              {/* Remarks */}
              <div style={s.cell}>
                {milestone.isNew
                  ? <input
                    type="text"
                    style={s.remarksInput}
                    placeholder="Add remarks…"
                    value={milestone.remarks || ""}
                    onChange={e => handleChange(milestone._id, "remarks", e.target.value)}
                  />
                  : <input
                    type="text"
                    style={{ ...s.remarksInput, ...s.remarksDisabled }}
                    value={milestone.remarks || "N/A"}
                    disabled
                  />
                }
              </div>

              {/* Attachment */}
              <div style={s.cell}>
                {milestone.isNew && (
                  <label style={s.uploadBtn}>
                    <CloudUploadIcon sx={{ fontSize: 13 }} />
                    Upload
                    <input
                      type="file" hidden multiple
                      onChange={e => e.target.files && handleFileUpload(milestone._id, e.target.files)}
                    />
                  </label>
                )}
                {milestone.files.length > 0
                  ? (milestone.files || []).map((f: FileWithMeta, idx: number) => (
                    <div key={idx} style={s.fileItem}>
                      {f.url ? (
                        <>
                          <FileText size={11} color="#2563eb" style={{ flexShrink: 0 }} />
                          <a href={f.url} target="_blank" rel="noreferrer" style={s.fileLink}
                            title={decodeURIComponent(new URL(f.url).pathname.split("/").pop() || "")}>
                            {decodeURIComponent(new URL(f.url).pathname.split("/").pop() || "")}
                          </a>
                          {milestone.isNew && (
                            <button style={s.fileDelBtn} onClick={() => handleFileDelete(milestone._id, f.url)}>
                              <X size={11} />
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ width: "100%" }}>
                          <span style={s.progressText}>{f.file?.name} · {f.progress || 0}%</span>
                          <div style={s.progressWrap}>
                            <div style={{ ...s.progressWrap, background: "#F8B71F", width: `${f.progress || 0}%`, margin: 0 }} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                  : (!milestone.isNew && (
                    <input
                      type="text"
                      style={{ ...s.remarksInput, ...s.remarksDisabled }}
                      value="N/A"
                      disabled
                    />
                  ))
                }
              </div>

              {/* ── Comment icon ── */}
              {milestone.isCommentOf==false&&<div style={{ ...s.cell, display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
                <button
                  style={{
                    ...s.commentIconBtn,
                    ...(activeDrawerMilestone?.id === milestone._id && drawerOpen
                      ? { background: "#fef9c3", color: "#d97706" }
                      : {}),
                  }}
                  onClick={() => openCommentDrawer(milestone._id!, milestone.name)}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = "#fef9c3";
                    (e.currentTarget as HTMLButtonElement).style.color = "#d97706";
                  }}
                  onMouseLeave={e => {
                    if (activeDrawerMilestone?.id !== milestone._id || !drawerOpen) {
                      (e.currentTarget as HTMLButtonElement).style.background = "#f3f4f6";
                      (e.currentTarget as HTMLButtonElement).style.color = "#9ca3af";
                    }
                  }}
                  title="View / add comments"
                >
                  <MessageSquare size={14} />
                  {commentCount > 0 && (
                    <span style={s.commentBadge}>
                      {commentCount > 9 ? "9+" : commentCount}
                    </span>
                  )}
                </button>
              </div>}

            </div>

            {/* Row actions (delete) — shown on hover */}
            {isHovered && (
              <RowActions
                showDelete={firstmilestonesFormData.length > 1}
                showAdd={false}
                onAdd={handleAdd}
                onDelete={() => handleDelete(milestone._id)}
              />
            )}
          </div>
        );
      })}

      {/* Add row button */}
      {!groupMilestone && (
        <button
          style={s.addRowBtn}
          onClick={handleAdd}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#F8B71F";
            (e.currentTarget as HTMLButtonElement).style.color = "#374151";
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#d1d5db";
            (e.currentTarget as HTMLButtonElement).style.color = "#9ca3af";
          }}
        >
          + Add milestone
        </button>
      )}

      {/* Footer */}
      <div style={s.footer}>
        <span style={s.footerLeft}>
          {isSaving
            ? "Saving changes…"
            : newCount > 0
              ? <><strong style={{ color: "#374151" }}>{newCount}</strong> unsaved change{newCount !== 1 ? "s" : ""}</>
              : "All changes saved"
          }
        </span>
        <button
          style={{ ...s.saveBtn, ...(isSaveDisabled ? s.saveBtnDisabled : {}) }}
          disabled={isSaveDisabled}
          onClick={handleSave}
        >
          <Save size={13} />
          {isSaving ? "Saving…" : "Save Milestones"}
        </button>
      </div>

      {/* ── Comment Drawer (reusable) ── */}
      <MilestoneCommentDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setActiveDrawerMilestone(null); }}
        milestoneName={activeDrawerMilestone?.name || ""}
        milestoneId={activeDrawerMilestone?.id || ""}
        fetchComments={fetchCommentsForMilestone}
        saveComment={saveCommentForMilestone}
        deleteComment={deleteCommentForMilestone}
        uploadFile={uploadFileForComment}
      />
    </div>
  );
}

export default TabFirstMile;